import styled from "styled-components";
import Link from "next/link";
import Image from "next/image";
import Text from "@atlas/uicorn/lib/Display/Text";
import MessageIcon from "@atlas/uicorn/lib/Icon/MessageIcon";
import Conversation, { DeliveryType } from "../../../models/conversation";
import EnvelopeIcon from "@atlas/uicorn/lib/Icon/EnvelopeIcon";
import PhoneIcon from "@atlas/uicorn/lib/Icon/PhoneIcon";
import UserAvatar from "../../user-avatar";
import whatsappIcon from "../../../public/icons/whatsapp.png";
import slackIcon from "../../../public/icons/slack-logo.png";
import discordIcon from "../../../public/icons/discord-logo.png";
import Tooltip from "@atlas/uicorn/lib/Display/Tooltip";
import { shortDateTimeDisplay } from "@atlas/tool/box/date/time-ts";

export default function ConversationListItem({
  conversation,
  baseUrl,
  showCustomer,
  numbersLength,
  namesLength,
}: {
  conversation: Conversation;
  baseUrl: string;
  showCustomer?: boolean;
  numbersLength: number;
  namesLength: number;
}) {
  return (
    <SLink href={`${baseUrl}/customer-portal/${conversation.id}`}>
      {DeliveryTypeIcons[conversation.startedChannel]}
      {numbersLength > 0 && (
        <SNumber $characters={numbersLength + 1}>
          #{conversation.number}
        </SNumber>
      )}
      {showCustomer && (
        <SCustomerName $characters={namesLength < 12 ? namesLength : 12}>
          {conversation.customer.fullName}
        </SCustomerName>
      )}
      {conversation.lastMessage && (
        <SLastMessage>
          {conversation.subject || conversation.lastMessage.plainText}
        </SLastMessage>
      )}
      {conversation.assignedAgent?.fullName && (
        <>
          <Text>{conversation.assignedAgent.fullName}</Text>
          <UserAvatar user={conversation.assignedAgent} />
        </>
      )}

      <SDate>
        <Tooltip
          title={
            conversation.lastMessage &&
            `Last updated ${formatDateTime(conversation.lastMessage.sentAt)}`
          }
          placement="top"
        >
          <span>{conversation.lastMessage?.sentAtDisplay}</span>
        </Tooltip>
      </SDate>
    </SLink>
  );
}

function formatDateTime(date: string) {
  return shortDateTimeDisplay(date);
}

export const DeliveryTypeIcons = {
  [DeliveryType.CHAT]: <MessageIcon />,
  [DeliveryType.EMAIL]: <EnvelopeIcon />,
  [DeliveryType.SMS]: <PhoneIcon />,
  [DeliveryType.WHATSAPP]: (
    <Image width={18} src={whatsappIcon} alt="whatsapp" />
  ),
  [DeliveryType.SLACK_CONNECT]: (
    <Image width={18} src={slackIcon} alt="slack" />
  ),
  [DeliveryType.DISCORD_CONNECT]: (
    <Image width={18} src={discordIcon} alt="discord" />
  ),
};

const SCustomerName = styled(Text)<{ $characters: number }>`
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: ${(p) => p.$characters}ch;
`;

const SNumber = styled(Text)<{ $characters: number }>`
  min-width: ${(p) => p.$characters}ch;
`;

const SLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 8px;

  &:hover {
    background-color: ${(p) => p.theme.color.grey5};
  }
`;

const SLastMessage = styled(Text).attrs({ ellipsis: true })`
  flex: 1;
`;

const SDate = styled(Text)`
  width: 90px;
  text-align: right;
`;
